"use client";

import { c as _c } from "react/compiler-runtime";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import { CreateIssueButton } from "@/components/create-issue-button";
import { analytics } from "@/lib/analytics/client";
import { Button } from "@domainstack/ui/button";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@domainstack/ui/empty";
interface Props {
  children: React.ReactNode;
  /** Display name for the report section (e.g., "Registration", "DNS") */
  sectionName: string;
}
function SectionErrorFallback(t0) {
  const $ = _c(15);
  const {
    error,
    resetErrorBoundary
  } = t0;
  const isDev = process.env.NODE_ENV === "development";
  const errorObj = error instanceof Error ? error : undefined;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <EmptyMedia variant="icon"><IconAlertTriangle /></EmptyMedia>;
    t2 = <EmptyTitle>Something went wrong</EmptyTitle>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  const t3 = isDev && errorObj ? errorObj.message : "This section encountered an error and couldn't be displayed.";
  let t4;
  if ($[2] !== t3) {
    t4 = <EmptyHeader>{t1}{t2}<EmptyDescription>{t3}</EmptyDescription></EmptyHeader>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <IconRefresh />;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== resetErrorBoundary) {
    t6 = <Button variant="outline" size="sm" onClick={resetErrorBoundary}>{t5}Retry</Button>;
    $[5] = resetErrorBoundary;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== errorObj) {
    t7 = <CreateIssueButton error={errorObj} variant="outline" size="sm" />;
    $[7] = errorObj;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = <EmptyContent><div className="flex flex-wrap items-center justify-center gap-2">{t6}{t7}</div></EmptyContent>;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t4 || $[13] !== t8) {
    t9 = <Empty className="border border-dashed">{t4}{t8}</Empty>;
    $[12] = t4;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
}

/**
 * Error boundary for individual domain sections.
 * Catches rendering errors and provides a fallback UI without crashing the entire page.
 * Integrates with React Query to reset cached errors on retry.
 */
export function SectionErrorBoundary(t0) {
  const $ = _c(6);
  const {
    children,
    sectionName
  } = t0;
  const {
    reset
  } = useQueryErrorResetBoundary();
  let t1;
  if ($[0] !== sectionName) {
    t1 = (error, errorInfo) => {
      if (error instanceof Error) {
        analytics.trackException(error, {
          section: sectionName,
          componentStack: errorInfo.componentStack
        });
      }
    };
    $[0] = sectionName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== reset || $[4] !== t1) {
    t2 = <ErrorBoundary FallbackComponent={SectionErrorFallback} onReset={reset} onError={t1}>{children}</ErrorBoundary>;
    $[2] = children;
    $[3] = reset;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}